import { useTranslation } from 'react-i18next'
import { Dialog } from 'primereact/dialog'
import { Column } from 'primereact/column'
import Button from '../../../components/ui/Button'
import DataTable from '../../../components/ui/DataTable'
import type { ExcelImportPreview, ExcelRowError, ExcelSheetKind } from '../types'

interface Props {
  visible: boolean
  preview: ExcelImportPreview | null
  /** Ekranda hâlihazırda Excel'den aktarılmış satır var mı → replace uyarısı gösterilir (FR-022). */
  hasExistingExcelRows: boolean
  onCancel: () => void
  onTransfer: () => void
  /**
   * Aktarımın anlamı — pop-up'ın kullanıcıya ne vaat ettiğini belirler.
   *
   * - `replace` (varsayılan, **Tutanak Oluştur**): kayıt henüz yoktur; tekrar yükleme önceki Excel
   *   satırlarının yerine geçer, elle girilenler korunur.
   * - `append` (**Toplantı Detay**): maddeler zaten kayıtlıdır; satırlar mevcutların sonuna eklenir.
   *   Kaç satırın eklenaceği açıkça yazılır ({@link appendableRowCount}), çünkü aynı dosyanın ikinci
   *   yüklemesi maddeleri çiftler ve kullanıcı bunu onaylayarak yapar.
   */
  mode?: 'replace' | 'append'
  /** `append` modunda eklenecek satır sayısı (bölümü olmayan sayfaların satırları hariç). */
  appendableRowCount?: number
  /**
   * `append` modunda **aktarılamayan** satır sayısı (bu toplantıda karşılığı olan içerik bölümü yok).
   * Gövdede gösterilir; buton pasif olsa bile gerekçe görünsün diye {@link appendableRowCount}'tan
   * bağımsızdır.
   */
  skippedRowCount?: number
}

/**
 * Excel ön izleme pop-up'ı (088, US1/US2): sayfa başına geçerli satır sayısı + <b>satır/alan bazlı</b>
 * hata tablosu (analiz 1.11.8 — Sayfa · Satır · Alan · Hata) ve "Aktar" eylemi.
 *
 * <p>Hatalı satır bulunsa da <b>geçerli satırlar aktarılabilir</b> (kısmi aktarım); buton etiketi o duruma
 * göre değişir. Global `.p-dialog` standardına uyar (footer `pop-footer`, alt başlık `pop-subtitle`).
 */
export default function ExcelImportPreviewDialog({
  visible,
  preview,
  hasExistingExcelRows,
  onCancel,
  onTransfer,
  mode = 'replace',
  appendableRowCount = 0,
  skippedRowCount = 0,
}: Readonly<Props>) {
  const { t } = useTranslation()

  const errors: ExcelRowError[] = preview?.sheets.flatMap((s) => s.errors) ?? []
  const appendMode = mode === 'append'
  /**
   * `append` modunda ölçüt `totalValidRows` DEĞİL `appendableRowCount`'tur: bu toplantıda karşılığı
   * olmayan bir sayfanın (ör. Prensip bölümü yoksa) satırları aktarılamaz, dolayısıyla yalnız onlar
   * geldiyse "Aktar" da açılmamalıdır — aksi halde buton hiçbir şey yapmazdı.
   */
  const hasRows = appendMode ? appendableRowCount > 0 : (preview?.totalValidRows ?? 0) > 0
  const transferLabel =
    errors.length > 0 ? t('minutes.excel.transferValid') : t('minutes.excel.transfer')

  const footer = (
    <div className="pop-footer">
      <Button icon="pi pi-check" label={transferLabel} onClick={onTransfer} disabled={!hasRows} />
    </div>
  )

  return (
    <Dialog
      visible={visible}
      onHide={onCancel}
      header={t('minutes.excel.previewTitle')}
      footer={footer}
      modal
      dismissableMask
      style={{ width: '52rem', maxWidth: '95vw' }}
    >
      <p className="pop-subtitle">{t('minutes.excel.previewSubtitle')}</p>

      {preview && (
        <>
          <table className="tp-table">
            <thead>
              <tr>
                <th>{t('minutes.excel.errorColumn.sheet')}</th>
                <th>{t('minutes.excel.validRows')}</th>
                <th>{t('minutes.excel.errorRows')}</th>
              </tr>
            </thead>
            <tbody>
              {preview.sheets.map((sheet) => (
                <tr key={sheet.kind}>
                  <td className="tp-strong">
                    {t(`minutes.excel.sheet.${sheet.kind satisfies ExcelSheetKind}`)}
                  </td>
                  <td>{sheet.rows.length}</td>
                  <td>{new Set(sheet.errors.map((e) => e.rowNumber)).size}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {!hasRows && <p className="act-empty-hint">{t('minutes.excel.noRows')}</p>}

          {/* Atlanan satırların gerekçesi — "Aktar" pasifken de görünür (tek gerekçe kaynağı). */}
          {appendMode && skippedRowCount > 0 && (
            <p className="act-empty-hint">
              {t('minutes.excel.skippedNoSection', { count: skippedRowCount })}
            </p>
          )}

          {/* Aktarımın mevcut maddelere ne yapacağı — iki ekranda farklı, bu yüzden metin de farklı. */}
          {appendMode && hasRows && (
            <p className="act-empty-hint">
              {t('minutes.excel.appendNote', { count: appendableRowCount })}
            </p>
          )}

          {!appendMode && hasExistingExcelRows && hasRows && (
            <p className="act-empty-hint">{t('minutes.excel.replaceWarning')}</p>
          )}

          {errors.length > 0 && (
            <div className="tp-section">
              <h3 className="tp-section-title">{t('minutes.excel.errorsTitle')}</h3>
              <DataTable value={errors} dataKey="key" rows={5} rowsPerPageOptions={[5, 10, 20]}>
                <Column field="sheetName" header={t('minutes.excel.errorColumn.sheet')} />
                <Column field="rowNumber" header={t('minutes.excel.errorColumn.row')} />
                <Column field="field" header={t('minutes.excel.errorColumn.field')} />
                <Column field="message" header={t('minutes.excel.errorColumn.message')} />
              </DataTable>
            </div>
          )}
        </>
      )}
    </Dialog>
  )
}
